import { CandlestickData, ColorType, CrosshairMode, HistogramData, IChartApi, ISeriesApi, SeriesMarker, TickMarkType, Time, UTCTimestamp, createChart } from 'lightweight-charts'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { MONO } from '../ui'
import type { Candle } from '../types'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const two = (n: number) => String(n).padStart(2, '0')
/** Time-axis labels in UTC like the crosshair: "2026", "Mar", "Dec 8" (the library's own day label is a bare "8"), "14:30". */
export function tickLabel(time: Time, type: TickMarkType): string {
  const d = new Date(Number(time) * 1000)
  if (type === TickMarkType.Year) return String(d.getUTCFullYear())
  if (type === TickMarkType.Month) return MONTHS[d.getUTCMonth()]
  if (type === TickMarkType.DayOfMonth) return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`
  return `${two(d.getUTCHours())}:${two(d.getUTCMinutes())}`
}

/**
 * Price text inside the chart (axis, crosshair, legend). Tiny prices are written in scientific notation ("5.47e-6"): the subscript-zero style
 * used in the page header at 44 px turns into unreadable 7 px digits at chart size.
 */
export function chartPrice(n: number): string {
  // below 1e-20 is the axis' own zero with a rounding crumb (3.31e-24), not a price
  if (!Number.isFinite(n) || n < 1e-20) return ''
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
  if (n >= 1) return Number(n.toFixed(4)).toString()
  if (n >= 0.001) return Number(n.toPrecision(4)).toString()
  const [mantissa, exp] = n.toExponential(2).split('e')
  return `${Number(mantissa)}e${exp}`
}

const Box = styled.div<{ $h: number }>`
  position: relative;
  width: 100%;
  height: ${({ $h }) => $h}px;
  min-width: 0;
`
const Legend = styled.div`
  position: absolute;
  z-index: 2;
  top: 6px;
  left: 8px;
  right: 72px; /* clear of the price axis */
  display: flex;
  flex-wrap: wrap;
  gap: 2px 12px;
  pointer-events: none;
  font-family: ${MONO};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSubtle};
  b {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text};
  }
`

/** A moment to mark on the chart (a competition's start or end). It sits on the candle that contains it; one before the first candle is not drawn. */
export type ChartMark = { t: number; label: string; tone: 'start' | 'end' }

export function marksOnCandles(candles: Candle[], marks: ChartMark[]): Array<SeriesMarker<Time>> {
  if (!candles.length) return []
  const times = candles.map((c) => c.t)
  const out = new Map<number, { text: string; tone: ChartMark['tone'] }>()
  marks.forEach((m) => {
    let hit = -1
    for (let i = 0; i < times.length; i += 1) if (times[i] <= m.t) hit = i
    if (hit < 0 || m.t > times[times.length - 1] + (times.length > 1 ? times[1] - times[0] : 3600)) return
    const prev = out.get(times[hit])
    out.set(times[hit], { text: prev ? `${prev.text} / ${m.label}` : m.label, tone: prev ? 'end' : m.tone })
  })
  return Array.from(out.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([t, v]) => ({ time: t as UTCTimestamp, position: 'aboveBar', shape: 'arrowDown', color: v.tone === 'start' ? '#39D98A' : '#FFC24A', text: v.text }))
}

type Props = { candles: Candle[]; height?: number; unit?: 'USD' | 'quote'; quoteSymbol?: string; marks?: ChartMark[] }

/** Candlesticks with volume (TradingView Lightweight Charts). Client only; resizes with its box; colours come from the theme's success and failure. */
export default function PriceChart({ candles, height = 320, unit = 'USD', quoteSymbol, marks }: Props) {
  const host = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi>()
  const candleRef = useRef<ISeriesApi<'Candlestick'>>()
  const volRef = useRef<ISeriesApi<'Histogram'>>()
  const [hover, setHover] = useState<Candle | null>(null)
  const byTime = useMemo(() => new Map(candles.map((c) => [c.t, c])), [candles])
  const heightRef = useRef(height)
  const byTimeRef = useRef(byTime)
  byTimeRef.current = byTime
  const last = candles[candles.length - 1]
  const shown = hover ?? last
  const prefix = unit === 'USD' ? '$' : ''

  useEffect(() => {
    if (!host.current) return undefined
    const chart = createChart(host.current, {
      width: host.current.clientWidth,
      height: heightRef.current,
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: 'rgba(214, 222, 240, 0.92)', fontFamily: "'Martian Mono', ui-monospace, monospace", fontSize: 12 },
      grid: { vertLines: { color: 'rgba(180, 200, 255, 0.05)' }, horzLines: { color: 'rgba(180, 200, 255, 0.07)' } },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.14, bottom: 0.24 } },
      timeScale: { borderVisible: false, timeVisible: true, secondsVisible: false, rightOffset: 3, fixLeftEdge: false, tickMarkFormatter: tickLabel },
      crosshair: { mode: CrosshairMode.Normal, vertLine: { color: 'rgba(127, 212, 232, 0.5)', labelBackgroundColor: '#11162C' }, horzLine: { color: 'rgba(127, 212, 232, 0.5)', labelBackgroundColor: '#11162C' } },
      localization: { priceFormatter: chartPrice },
      handleScale: { mouseWheel: false, pinch: true, axisPressedMouseMove: true },
      handleScroll: { mouseWheel: false, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
    })
    const candle = chart.addCandlestickSeries({
      upColor: '#39D98A', downColor: '#FF6B7A', borderUpColor: '#39D98A', borderDownColor: '#FF6B7A', wickUpColor: '#39D98A', wickDownColor: '#FF6B7A',
      priceFormat: { type: 'custom', formatter: chartPrice, minMove: 1e-12 },
      // the last price is in the legend and in the page header; a tag on the axis only collides with the ticks next to it
      lastValueVisible: false,
      // a dormant pool draws a flat line: give the axis at least +-2 % so its labels are distinct numbers, not one repeated value
      autoscaleInfoProvider: (original: () => { priceRange: { minValue: number; maxValue: number } } | null) => {
        const r = original()
        if (!r) return r
        const { minValue, maxValue } = r.priceRange
        const mid = (minValue + maxValue) / 2
        return maxValue - minValue < mid * 0.04 ? { ...r, priceRange: { minValue: mid * 0.98, maxValue: mid * 1.02 } } : r
      },
    })
    const vol = chart.addHistogramSeries({ priceFormat: { type: 'volume' }, priceScaleId: 'vol', lastValueVisible: false, priceLineVisible: false })
    chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 }, visible: false })
    chart.subscribeCrosshairMove((p) => {
      const t = p.time as number | undefined
      setHover(t && p.point ? byTimeRef.current.get(t) ?? null : null)
    })
    chartRef.current = chart
    candleRef.current = candle
    volRef.current = vol
    const ro = new ResizeObserver(() => {
      if (host.current && host.current.clientWidth > 0) chart.applyOptions({ width: host.current.clientWidth })
    })
    ro.observe(host.current)
    return () => {
      ro.disconnect()
      chart.remove()
      chartRef.current = undefined
    }
    // the chart is created once; its height and its data are applied by the effects below, so it is never re-created empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    heightRef.current = height
    chartRef.current?.applyOptions({ height })
  }, [height])

  useEffect(() => {
    if (!candleRef.current || !volRef.current) return
    candleRef.current.setData(candles.map((c) => ({ time: c.t as UTCTimestamp, open: c.o, high: c.h, low: c.l, close: c.c })) as CandlestickData[])
    volRef.current.setData(candles.map((c) => ({ time: c.t as UTCTimestamp, value: c.v, color: c.c >= c.o ? 'rgba(57, 217, 138, 0.32)' : 'rgba(255, 107, 122, 0.32)' })) as HistogramData[])
    candleRef.current.setMarkers(marks ? marksOnCandles(candles, marks) : [])
    // the candles fill the width with a few bars of air on the left, so the first time label is never cut by the chart's edge
    const n = candles.length
    // air on both sides: the first time label is never cut at the left edge, and a start or end marker on the last candle has room for its text
    if (n > 1) chartRef.current?.timeScale().setVisibleLogicalRange({ from: -Math.max(2, n * 0.04), to: n - 1 + Math.max(3, n * 0.07) })
    else chartRef.current?.timeScale().fitContent()
  }, [candles, marks])

  return (
    <Box $h={height} data-testid="tp-chart" data-candles={candles.length}>
      {shown && (
        <Legend aria-hidden="true">
          <span>O <b>{prefix}{chartPrice(shown.o)}</b></span>
          <span>H <b>{prefix}{chartPrice(shown.h)}</b></span>
          <span>L <b>{prefix}{chartPrice(shown.l)}</b></span>
          <span>C <b>{prefix}{chartPrice(shown.c)}</b></span>
          {unit === 'quote' && quoteSymbol && <span>in <b>{quoteSymbol}</b></span>}
        </Legend>
      )}
      <div ref={host} style={{ position: 'absolute', inset: 0 }} />
    </Box>
  )
}
